// src/components/NotificationSetup.js
import React, { useEffect } from 'react';
import axios from 'axios';
import { useSelector } from "react-redux";

const NotificationSetup = ({ companyId, branchId }) => {

    const BACKEND_API_URL = process.env.REACT_APP_BASEURL;
    const PUBLIC_VAPID_KEY = process.env.REACT_APP_PUSH_VAPID_PUBLIC_KEY;
    const userInfo = useSelector((state) => state.authReducer);

    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const sendSubscriptionToBackend = async (subscription) => {
        try {
            const subscriptionPayload = {
                ...subscription.toJSON(),
                CompanyId: userInfo?.CompanyId,
                BranchId: userInfo?.BranchId || null,
            };

            // console.log('userBranchList:', userInfo.branchId);

            const res = await axios.post(`/subscribe`, subscriptionPayload);

            console.log('NotificationSetup: Subscription response from backend:', res.data);

            // Check if subscription was successful
            if (res.data?.Response === true) {
                const action = res.data?.DataSet?.Table?.[0]?.Action;
                console.log(`NotificationSetup: Subscription ${action || 'saved'} successfully for CompanyId: ${companyId}, BranchId: ${branchId}`);

                // Log subscription details
                if (res.data?.DataSet?.Table?.[0]) {
                    console.log('NotificationSetup: Subscription details:', res.data.DataSet.Table[0]);
                }
            } else {
                console.error("NotificationSetup: Subscription failed", res.data);
            }

            console.log('NotificationSetup: Subscription sent to backend successfully.');
            return true;
        } catch (error) {
            if (error.response) {
                // Server responded with error status
                console.error('NotificationSetup: Server error:', error.response.status, error.response.data?.errorMessage || error.response.data);
            } else if (error.request) {
                // Request made but no response received
                console.error('NotificationSetup: No response from server. Check if backend is running.');
            } else {
                // Error in request setup
                console.error('NotificationSetup: Error setting up request:', error.message);
            }
            console.error('NotificationSetup: Failed to send subscription to backend:', error);
            return false;
        }
    };

    useEffect(() => {
        if (!companyId) {
            console.warn('NotificationSetup: CompanyId is required.');
            return;
        }

        const setupPushNotifications = async () => {

            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                console.warn('Notifications not supported by this browser.');
                return;
            }

            try {
                // Register service worker
                const registration = await navigator.serviceWorker.register('/sw.js');
                console.log('NotificationSetup: Service Worker registered.');

                // Wait for service worker to be ready
                await registration.update();

                // Request notification permission if not granted
                if (Notification.permission !== 'granted') {
                    const permission = await Notification.requestPermission();
                    if (permission !== 'granted') {
                        console.warn('NotificationSetup: Permission denied.');
                        return;
                    }
                }

                // Check if we already have a subscription
                let subscription = await registration.pushManager.getSubscription();

                // If no subscription exists, create one
                if (!subscription) {
                    // Convert VAPID key to Uint8Array for better browser compatibility
                    const applicationServerKey = urlBase64ToUint8Array(PUBLIC_VAPID_KEY);

                    subscription = await registration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: applicationServerKey
                    });
                    console.log('NotificationSetup: New push subscription created.');
                } else {
                    console.log('NotificationSetup: Existing push subscription found.');
                }

                // Send subscription to backend (always send to ensure backend has latest subscription)
                await sendSubscriptionToBackend(subscription);

            } catch (error) {
                console.error('NotificationSetup: Error setting up notifications:', error);
                // If subscription fails, try to handle common errors
                if (error.name === 'NotAllowedError') {
                    console.warn('NotificationSetup: User denied permission or browser blocked notifications.');
                } else if (error.name === 'AbortError') {
                    console.warn('NotificationSetup: Subscription was aborted.');
                }
            }
        };

        setupPushNotifications();

        // Re-subscribe if subscription expires (check every hour)
        const subscriptionCheckInterval = setInterval(async () => {
            try {
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                if (!subscription && Notification.permission === 'granted') {
                    console.log('NotificationSetup: Subscription expired, re-subscribing...');
                    setupPushNotifications();
                }
            } catch (error) {
                console.error('NotificationSetup: Error checking subscription:', error);
            }
        }, 3600000); // Check every hour

        return () => {
            clearInterval(subscriptionCheckInterval);
        };

    }, [companyId, branchId]);

    if (Notification.permission === 'denied') {
        return (
            <div style={{
                padding: '10px',
                backgroundColor: '#ffdddd',
                border: '1px solid #ffaaaa',
                margin: '10px 0',
                borderRadius: '4px'
            }}>
                <strong>🔔 Notifications Blocked!</strong>
                <p style={{ margin: '5px 0 0 0' }}>
                    Please enable notifications in your browser settings to receive new order alerts even when the browser is closed.
                </p>
            </div>
        );
    }
    return null;
};

export default NotificationSetup;